import type {
  ListDeploymentsResponse,
  DeploymentSummaryDto,
  DeploymentKind,
  DeploymentReason,
  ReturnDeploymentRequest,
  DeploymentReturnReason,
  RobotReturnLocation,
} from '@bhops/shared';
import { apiClient } from './client';

export interface ListDeploymentsQuery {
  robotId?: string;
  storeId?: string;
  customerId?: string;
  kind?: DeploymentKind;
  activeOnly?: boolean;
  page?: number;
  pageSize?: number;
}

/**
 * 원클릭 교체(2026-08-27 요구 2)의 입력. 서버 SwapDeploymentDto 와 짝.
 * 사유·장소·메모를 생략하면 서버가 회수 창과 **같은 기본값**을 쓴다
 * (사유=고장 교체, 장소=A/S센터) — 화면이 기본값을 따로 들고 있으면 갈린다.
 */
export interface SwapDeploymentRequest {
  newRobotId: string;
  returnedAt: string;
  returnReason?: DeploymentReturnReason;
  returnLocation?: RobotReturnLocation;
  notes?: string;
}

export interface SwapDeploymentResponse {
  returnedDeploymentId: string;
  newDeployment: DeploymentSummaryDto;
  /** 새 로봇이 이어받은 계약. null = 원래 예약이 없던 배치(옛 데이터). */
  carriedContractId: string | null;
  customerRetired: boolean;
}

export interface CreateDeploymentBody {
  robotId: string;
  // 서버는 이 값을 생략해도 받는다(create-deployment.dto) — 그러면 customerId 로
  // 매장을 해소한다: 기존 매장 중 **가장 오래된 것** → 없으면 고객사명으로 생성.
  // 콘솔은 일부러 그 길을 쓰지 않고 늘 보낸다. 지점이 여럿인 고객사(지점마다 계약이
  // 따로다)에서 '가장 오래된 매장'은 조용히 틀린 지점이 되기 때문이다. 콘솔이
  // 스스로 정한 규칙이지 서버의 제약이 아니다 — 서버 쪽을 고칠 때 이 줄을 근거로
  // 삼지 말 것.
  storeId: string;
  customerId?: string;
  kind?: DeploymentKind;
  reason?: DeploymentReason;
  deployedAt: string;
  /** 2026-10-26 — 스페어(예비) 로봇으로 지급. 고객 배치에만 적힌다. */
  isSpare?: boolean;
  eventName?: string;
  eventStart?: string;
  eventEnd?: string;
  eventPurpose?: string;
  eventLocation?: string;
  notes?: string;
}

export const deploymentsApi = {
  async list(query: ListDeploymentsQuery = {}): Promise<ListDeploymentsResponse> {
    const { data } = await apiClient.get<ListDeploymentsResponse>('/deployments', {
      params: query,
    });
    return data;
  },
  async downloadAcceptancePdf(id: string): Promise<void> {
    const res = await apiClient.get<Blob>(`/deployments/${id}/acceptance-pdf`, {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    const disposition = res.headers['content-disposition'] as string | undefined;
    a.download =
      disposition?.match(/filename="([^"]+)"/)?.[1] ?? `acceptance-${id.slice(0, 8)}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  },
  async create(body: CreateDeploymentBody): Promise<DeploymentSummaryDto> {
    const { data } = await apiClient.post<DeploymentSummaryDto>('/deployments', body);
    return data;
  },
  // 개선계획 §7 — 회수 사유/처분을 함께 보낸다. 위치 인자(returnedAt, notes)로는
  // 새 필드가 붙을 때마다 호출처가 전부 깨져서 body 객체로 전환.
  async return(id: string, body: ReturnDeploymentRequest): Promise<DeploymentSummaryDto> {
    const { data } = await apiClient.post<DeploymentSummaryDto>(`/deployments/${id}/return`, body);
    return data;
  },

  /**
   * 계획서(2026-08-27) 요구 2 — **원클릭 교체**.
   *
   * 회수 → 계약 승계 → 새 배치가 서버의 **한 트랜잭션**이다. 화면이 세 번 호출해
   * 순서를 지키던 종전 방식은 중간에 실패하면 반쪽 상태(계약 없는 로봇)를 남겼다.
   */
  async swap(id: string, body: SwapDeploymentRequest): Promise<SwapDeploymentResponse> {
    const { data } = await apiClient.post<SwapDeploymentResponse>(`/deployments/${id}/swap`, body);
    return data;
  },

  /**
   * 2026-10-20 — 잘못한 배치 되돌리기. 회수(나갔다 돌아옴)가 아니라 '나가지 않았다'로 만든다 —
   * 로봇은 배치 직전 상태로 돌아가 운영 중에서 빠지고, 교체로 들어간 배치면 뺐던 로봇이 다시 그 매장에.
   */
  async undo(
    id: string,
    body: { reason?: string } = {},
  ): Promise<{
    ok: boolean;
    robotStatus: string;
    restoredDeploymentId: string | null;
    restoredRobotId: string | null;
  }> {
    const { data } = await apiClient.post(`/deployments/${id}/undo`, body);
    return data;
  },

  /**
   * 2026-10-26 — 나가 있는 고객 배치에 스페어(예비 로봇) 표시를 켜고 끈다. 표시만 바뀌고 로봇 · 계약은 그대로.
   */
  async setSpare(id: string, isSpare: boolean): Promise<{ id: string; isSpare: boolean }> {
    const { data } = await apiClient.patch(`/deployments/${id}/spare`, { isSpare });
    return data;
  },

  /**
   * 2026-10-21 — 잘못 누른 회수 되돌리기. 배치는 다시 나가 있고 로봇은 그 매장에 운영 중으로 — 풀었던 계약
   * 예약 · 자동 종료한 고객사 · 고장 조치 이력 · 아직 손대지 않은 회수 정비 오더도 함께 되돌린다.
   */
  async undoReturn(
    id: string,
    body: { reason?: string } = {},
  ): Promise<{
    ok: boolean;
    robotStatus: string;
    customerReopened: boolean;
    reservedContractId: string | null;
    cancelledMaintenanceOrder: string | null;
    keptMaintenanceOrder: string | null;
  }> {
    const { data } = await apiClient.post(`/deployments/${id}/undo-return`, body);
    return data;
  },
};
